// SliderComponent.js

import React, { useState, useEffect, useRef, useContext } from 'react';
import { Slider, Typography, Box, Button } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import AppContext from '../../AppContext';
import './SliderComponent.css';

const SliderComponent = ({ onStop, onRecordingStatusChange }) => {
  const { sliderValue, setSliderValue, valuesArray, setValuesArray, isRecording, setIsRecording } = useContext(AppContext);
  const intervalRef = useRef(null);
  const sliderValueRef = useRef(sliderValue);
  const [formattedTime, setFormattedTime] = useState('00m 00s');

  useEffect(() => {
    setSliderValue(0);
    setValuesArray([]);
  }, []); // Run only once on mount

  useEffect(() => {
    sliderValueRef.current = sliderValue;
  }, [sliderValue]);

  useEffect(() => {
    if (onRecordingStatusChange) {
      onRecordingStatusChange(isRecording);
    }
  }, [isRecording, onRecordingStatusChange]);

  const startInterval = () => {
    setSliderValue(0);
    sliderValueRef.current = 0;
    setIsRecording(true);
    clearInterval(intervalRef.current);
    setValuesArray([0]);
    let timeCounter = 0;

    intervalRef.current = setInterval(() => {
      timeCounter += 1;
      setValuesArray((prevValues) => {
        const newValues = [...prevValues, sliderValueRef.current];
        formatTime(timeCounter);
        return newValues;
      });
    }, 1000);
  };

  const stopInterval = () => {
    clearInterval(intervalRef.current);
    setIsRecording(false);
    if (onStop) {
      onStop(valuesArray);
    }
  };

  const reset = () => {
    clearInterval(intervalRef.current);
    setSliderValue(0);
    setValuesArray([]);
    setIsRecording(false);
    setFormattedTime('00m 00s');
    if (onRecordingStatusChange) {
      onRecordingStatusChange(false);
    }
  };

  const handleSliderChange = (event, newValue) => {
    setSliderValue(newValue);
  };

  const formatTime = (totalSeconds) => {
    const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
    const seconds = (totalSeconds % 60).toString().padStart(2, '0');
    setFormattedTime(`${minutes}m ${seconds}s`);
  };

  let graphArray;

  if (valuesArray.length >= 16) {
    graphArray = valuesArray.slice(-16).map((value, index) => ({
      index: valuesArray.length - 16 + index,
      value,
    }));
  } else {
    graphArray = valuesArray.map((value, index) => ({
      index,
      value,
    }));
  }

  const maxTime = Math.max(15, valuesArray.length);
  const startTime = Math.max(0, valuesArray.length - 15);

  const ticks = Array.from({ length: maxTime - startTime + 1 }, (_, i) => startTime + i);

  // Modify formatXAxisTick to avoid calling formatTime
  const formatXAxisTick = (tick) => {
    if (tick % 5 === 0) {
      const totalSeconds = tick;
      const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
      const seconds = (totalSeconds % 60).toString().padStart(2, '0');
      if (minutes === '00') {
        return `${seconds}"`;
      }
      return `${minutes}'${seconds}"`;
    }
    return '';
  };

  return (
    <div className="slider-graph-box">
      <div className="main-container">
        <div className="slider-component-left-col">
          <Box className="slider-container">
            <Slider
              orientation="vertical"
              value={sliderValue}
              onChange={handleSliderChange}
              min={0}
              max={10}
              step={1}
              marks
              sx={{
                '& .MuiSlider-track': { height: 8 },
                '& .MuiSlider-rail': { height: 8 },
                '& .MuiSlider-thumb': { width: 24, height: 24 },
              }}
              style={{ height: '100%' }}
            />
          </Box>
          <Typography>Laugh: {sliderValue}</Typography>
        </div>
        <div className="slider-component-right-col">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={graphArray}>
              <XAxis
                dataKey="index"
                type="number"
                domain={[startTime, maxTime]}
                ticks={ticks}
                tickFormatter={formatXAxisTick} // Use the modified function
              />
              <YAxis domain={[0, 10]} />
              <Tooltip />
              <Area
                type="monotone"
                dataKey="value"
                stroke="#8884d8"
                fill="#8884d8"
                strokeWidth={6}
                isAnimationActive={false}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <Box className="button-container">
        {isRecording ? (
          <Button variant="contained" color="secondary" onClick={stopInterval}>
            Stop
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={startInterval}>
            Start
          </Button>
        )}
        <div className="formatted-time">{formattedTime}</div>
        <Button variant="contained" onClick={reset} disabled={isRecording}>
          Reset
        </Button>
      </Box>
    </div>
  );
};

export default SliderComponent;
